import { supabase } from '../../lib/supabase';
import {
  EmailPasswordAuthProvider,
  AuthUser,
  AuthSession,
  AuthResponse,
} from './providerInterface';
import { User, Session } from '@supabase/supabase-js';
import { authLogger } from '../../utils/loggerInstance';
import { handleSessionError } from './authFactory';

/**
 * Maps a Supabase user to our generic AuthUser interface
 */
function mapSupabaseUser(user: User | null): AuthUser | null {
  if (!user) return null;

  const {
    id,
    email,
    email_confirmed_at,
    created_at,
    updated_at,
    user_metadata,
    app_metadata,
    ...rest
  } = user;

  return {
    id,
    email: email || null,
    email_confirmed_at: email_confirmed_at || null,
    created_at: created_at || null,
    updated_at: updated_at || null,
    user_metadata: user_metadata || {},
    app_metadata: app_metadata || {},
    // Include any other properties that don't conflict
    ...rest,
  };
}

/**
 * Maps a Supabase session to our generic AuthSession interface
 */
function mapSupabaseSession(session: Session | null): AuthSession | null {
  if (!session) return null;

  const { access_token, refresh_token, expires_at, user, ...rest } = session;

  if (!access_token || typeof expires_at !== 'number') {
    return null;
  }

  const mappedUser = user ? mapSupabaseUser(user) : undefined;

  return {
    access_token,
    refresh_token: refresh_token || undefined,
    expires_at,
    user: mappedUser || undefined,
    // Include any other properties that don't conflict
    ...rest,
  };
}

/**
 * Supabase authentication provider implementation
 */
class SupabaseAuthProvider implements EmailPasswordAuthProvider {
  async signInWithPassword(email: string, password: string): Promise<AuthResponse> {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });

      if (error) {
        authLogger.error('Supabase sign-in error:', error);
        handleSessionError(error);
      }

      return {
        data: {
          user: mapSupabaseUser(data?.user || null),
          session: mapSupabaseSession(data?.session || null),
        },
        error,
      };
    } catch (error) {
      authLogger.error('Unexpected error during sign-in:', error);
      handleSessionError(error);
      return {
        data: { user: null, session: null },
        error: error instanceof Error ? error : new Error('Unknown sign-in error'),
      };
    }
  }

  async signUpWithPassword(email: string, password: string): Promise<AuthResponse> {
    try {
      const { data, error } = await supabase.auth.signUp({ email, password });

      // For signups, set the new_user_signup flag
      sessionStorage.setItem('new_user_signup', 'true');

      if (error) {
        authLogger.error('Supabase sign-up error:', error);
      }

      return {
        data: {
          user: mapSupabaseUser(data?.user || null),
          session: mapSupabaseSession(data?.session || null),
        },
        error,
      };
    } catch (error) {
      authLogger.error('Unexpected error during sign-up:', error);
      return {
        data: { user: null, session: null },
        error: error instanceof Error ? error : new Error('Unknown sign-up error'),
      };
    }
  }

  async signOut(): Promise<{ error: Error | null }> {
    try {
      const { error } = await supabase.auth.signOut();

      if (error) {
        authLogger.error('Supabase sign-out error:', error);
      }

      // Clear session data regardless of success/failure
      localStorage.removeItem('auth_session');
      localStorage.removeItem('csrf_token');

      return { error };
    } catch (error) {
      authLogger.error('Unexpected error during sign-out:', error);
      return {
        error: error instanceof Error ? error : new Error('Unknown sign-out error'),
      };
    }
  }

  async getUser(): Promise<{ data: { user: AuthUser | null } }> {
    try {
      const { data, error } = await supabase.auth.getUser();

      if (error) {
        authLogger.error('Supabase get-user error:', error);
        handleSessionError(error);
        return { data: { user: null } };
      }

      return {
        data: {
          user: mapSupabaseUser(data?.user || null),
        },
      };
    } catch (error) {
      authLogger.error('Unexpected error getting user:', error);
      handleSessionError(error);
      return { data: { user: null } };
    }
  }

  async getSession(): Promise<{ data: { session: AuthSession | null } }> {
    try {
      const { data, error } = await supabase.auth.getSession();

      if (error) {
        authLogger.error('Supabase get-session error:', error);
        handleSessionError(error);
        return { data: { session: null } };
      }

      return {
        data: {
          session: mapSupabaseSession(data?.session || null),
        },
      };
    } catch (error) {
      authLogger.error('Unexpected error getting session:', error);
      handleSessionError(error);
      return { data: { session: null } };
    }
  }

  async refreshSession(): Promise<AuthResponse> {
    try {
      const { data, error } = await supabase.auth.refreshSession();

      if (error) {
        authLogger.error('Supabase refresh-session error:', error);
        handleSessionError(error);
      }

      return {
        data: {
          user: mapSupabaseUser(data?.user || null),
          session: mapSupabaseSession(data?.session || null),
        },
        error,
      };
    } catch (error) {
      authLogger.error('Unexpected error refreshing session:', error);
      handleSessionError(error);
      return {
        data: { user: null, session: null },
        error: error instanceof Error ? error : new Error('Unknown refresh error'),
      };
    }
  }

  onAuthStateChange(callback: (event: string, session: AuthSession | null) => void) {
    return supabase.auth.onAuthStateChange((event, supabaseSession) => {
      const mappedSession = mapSupabaseSession(supabaseSession);
      callback(event, mappedSession);
    });
  }

  getProviderName(): string {
    return 'supabase';
  }
}

export const supabaseProvider = new SupabaseAuthProvider();
